
import { useCallback, useEffect } from "react";
import { toast } from "sonner";
import { AuthStateType, AUTH_SETTINGS } from "./useAuthState";

/**
 * Custom hook to handle user activity tracking
 */
export const useActivityTracking = (
  authState: AuthStateType,
  signOut: () => Promise<void>
) => {
  const { user, isMounted, lastActivityRef, sessionCheckIntervalRef } = authState;

  // Record user activity to manage session timeouts
  const recordActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, [lastActivityRef]);

  // Set up activity tracking
  useEffect(() => {
    if (user) {
      // Monitor user activity
      const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
      events.forEach(event => {
        window.addEventListener(event, recordActivity);
      });
      
      // Set up session check interval
      sessionCheckIntervalRef.current = setInterval(() => {
        const inactiveTime = Date.now() - lastActivityRef.current;
        
        // If user has been inactive longer than the session timeout
        if (inactiveTime > AUTH_SETTINGS.sessionTimeout) {
          console.log('[Security] User inactive, signing out');
          signOut();
          toast.info("You've been signed out due to inactivity");
        }
      }, 60000); // Check every minute
      
      return () => {
        events.forEach(event => {
          window.removeEventListener(event, recordActivity);
        });
        
        if (sessionCheckIntervalRef.current) {
          clearInterval(sessionCheckIntervalRef.current);
        }
      };
    }
  }, [user, recordActivity, sessionCheckIntervalRef, signOut]);

  return { recordActivity };
};
